/**
 * Wearing the brand: the tenant's palette and faces, written onto `<html>` as `--a-*` variables.
 *
 * ## One namespace for every public page
 *
 * `brand-public` already does the hard part — validating every hex, deriving the surfaces, emitting
 * the `@font-face` rules — and takes a `prefix`. Until 2026-10-04 each public app passed its OWN
 * prefix (`links`, `store`, `subscribe`, and `x` with three renamed roles for `checkout`), because each
 * app had its own stylesheet written against its own names. anubis components read one namespace,
 * `--a-*`, so the four apps stop carrying four mappings of the same eleven colours.
 *
 * The faces are mapped the way every page wants them: `display` is the brand's `primary` face,
 * `body` its `secondary` (`PAGE_FONT_ALIASES` — `checkout` once shipped that pairing backwards).
 *
 * ## The words on the accent are the brand's white (2026-10-10)
 *
 * `--a-on-primary` is the colour of the words on anything filled with the primary — a primary action,
 * a pressed `Segmented` option. It is the brand's own white, never a contrast-computed ink. The owner,
 * 2026-10-09 and again 2026-10-10 (quantumalex's green settings toggles): "the font should have been
 * white (white as defined by brand)". Until then a `measured` rule picked the brand's DARK whenever it
 * reached WCAG AA on the fill, which put black type on both live brands.
 *
 * "The brand's white" is the paler of `white_color` and `dark_color`: a light brand swaps the two
 * (its `dark_color` is the pale ground), and its white is still the pale one.
 */
import {
  PAGE_FONT_ALIASES,
  applyBrandIcons,
  applyBrandTheme,
  brandVariables,
} from '@martincastroalvarez/brand-public';
import type { BrandIcons, ThemeOptions, ThemeSource } from '@martincastroalvarez/brand-public';
import {
  applyAppIdentity,
  applyBrandManifest,
  type AppIdentity,
  type ManifestBrand,
} from './manifest';

export { appMarkUrl } from './manifest';
export type { AppIdentity } from './manifest';

/** The variable namespace every anubis component reads. */
export const ANUBIS_PREFIX = 'a';

/** What dressing a page needs of a brand: its colours, its faces, and its icons. */
export type AnubisBrand = ThemeSource & BrandIcons & ManifestBrand;

const OPTIONS: ThemeOptions = {
  prefix: ANUBIS_PREFIX,
  fonts: PAGE_FONT_ALIASES,
  styleId: 'anubis-fonts',
};

/** WCAG 2.1 relative luminance of `#rgb`/`#rrggbb`; 0 for anything that is not one. */
export function luminance(hex: string): number {
  const raw = hex.replace('#', '').trim();
  const full = raw.length === 3 ? [...raw].map((c) => c + c).join('') : raw.slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(full)) return 0;
  const [r, g, b] = [0, 2, 4].map((at) => {
    const channel = Number.parseInt(full.slice(at, at + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

/** A hex colour as its three channels, 0-255; black for anything that is not `#rgb`/`#rrggbb`. */
function channels(hex: string): [number, number, number] {
  const raw = hex.replace('#', '').trim();
  const full = raw.length === 3 ? [...raw].map((c) => c + c).join('') : raw.slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(full)) return [0, 0, 0];
  return [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16)) as [
    number,
    number,
    number,
  ];
}

/** `share` of `colour` mixed into `base`, as `#rrggbb` — what CSS `color-mix(in srgb, …)` paints. */
export function mixHex(colour: string, share: number, base: string): string {
  const a = channels(colour);
  const b = channels(base);
  return (
    '#' +
    a
      .map((value, index) =>
        Math.round(value * share + (b[index] ?? 0) * (1 - share))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/** WCAG 2.1 contrast ratio between two colours, 1 to 21. */
export function contrast(one: string, two: string): number {
  const first = luminance(one);
  const second = luminance(two);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/** The white of a brand with no colours at all. */
const WHITE = '#ffffff';

/** The brand's white: the paler of its `white` and `dark` roles (a light brand swaps the two). */
export function brandWhite(white: string, dark: string): string {
  if (!white && !dark) return WHITE;
  return luminance(white || WHITE) >= luminance(dark || WHITE) ? white || WHITE : dark;
}

/** Every `--a-*` variable a brand resolves to, with the ink rule applied. Pure — used by tests. */
export function anubisVariables(brand: AnubisBrand | null | undefined): Record<string, string> {
  const variables = brandVariables(brand, OPTIONS);
  const at = (role: string): string => variables[`--${ANUBIS_PREFIX}-${role}`] ?? '';
  variables[`--${ANUBIS_PREFIX}-on-primary`] = brandWhite(at('white'), at('dark'));
  return variables;
}

/**
 * Put the brand on the document: the variables on `<html>`, the faces in one `<style>`, the tab icon.
 *
 * Idempotent, and cheap to repeat — which is what paint-then-revalidate does: once with the cached
 * brand on the first frame and again with the answer. The `@font-face` sheet is rewritten only when it
 * changes, so an identical brand does not re-trigger a font load.
 */
export function applyAnubisBrand(
  brand: AnubisBrand | null | undefined,
  app?: AppIdentity | null | undefined,
): void {
  if (typeof document === 'undefined') return;
  applyBrandTheme(brand, OPTIONS);
  // The brand's mark is the tab icon, the touch icon AND the installed app's icon: every anubis page
  // names itself after the brand, never after the app that drew it (2026-10-05) — unless the page IS
  // a product of its own with its own icon (`app`, 2026-10-18: `wall`), which keeps its name and icon
  // and wears the brand's palette and faces.
  // An app whose folder cannot be found (a page on localhost) falls back to the brand's mark.
  if (!app || !applyAppIdentity(app)) {
    applyBrandIcons(brand);
    applyBrandManifest(brand);
  }
  // After `applyBrandTheme`, which writes `brand-public`'s own measured ink; the brand's white wins.
  const name = `--${ANUBIS_PREFIX}-on-primary`;
  document.documentElement.style.setProperty(name, anubisVariables(brand)[name] ?? WHITE);
}
